import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ConversationMessageView } from '@mark43/cad-api';
import { useMessagingResource } from './use-messaging-resource';
import { ListStore } from '@list-store';
import { MessagingStore } from '../../store/messaging-store';

export const useBookmarkedConversationMessages = (store: MessagingStore) => {
    const {
        areBookmarkedConversationsFetched,
        selectedConversationKey,
        conversationMetadata,
    } = store;

    const { isScrolledToBottom } = conversationMetadata ?? {};

    const { getMessagesForConversation } = useMessagingResource();
    const queryKey = [
        'conversation-messages',
        selectedConversationKey && selectedConversationKey.toString(),
    ];
    const queryClient = useQueryClient();

    const getMessagesForBookmarkedConversationQuery = useQuery({
        queryKey,
        queryFn: async () => {
            if (selectedConversationKey === undefined) {
                throw new Error('conversationKey is undefined');
            }

            const { messageList } = queryClient.getQueryData<{
                messageList: ListStore<ConversationMessageView>;
            }>(queryKey) || { messageList: new ListStore<ConversationMessageView>() };

            const newMessages = await getMessagesForConversation({
                conversationKey: selectedConversationKey,
                bookmarksOnly: true,
            });

            if (newMessages.length > 0) {
                const previousMessageCount = messageList.data.list.length;
                messageList.upsertItems(newMessages);
                const newestMessage = newMessages[newMessages.length - 1];

                if (previousMessageCount === 0 || isScrolledToBottom) {
                    // scroll messages to bottom when new messages are fetched if there were previously
                    // no messages fetched or if the messages subpanel was previously scrolled to the bottom
                    const newestMessageIndex = messageList.getItemIndex(newestMessage.id);
                    if (newestMessageIndex !== undefined && selectedConversationKey !== undefined) {
                        store.setConversationScrollToIndex(
                            selectedConversationKey,
                            newestMessageIndex
                        );
                    }
                }
            }

            return { messageList }; // we return a new constructed object to avoid mutation only update
        },
        enabled:
            selectedConversationKey !== undefined &&
            selectedConversationKey.isBookmarkedConversationKey() &&
            areBookmarkedConversationsFetched,
        cacheTime: Infinity, // TODO make cache smaller
        staleTime: Infinity,
    });

    return getMessagesForBookmarkedConversationQuery;
};
